/**
 * Options for configuring throttle behavior
 */
export interface ThrottleOptions {
  /**
   * Execute the function immediately on the first call
   * @default true
   */
  leading?: boolean;

  /**
   * Execute the function after the wait period ends if there were calls during the wait
   * @default true
   */
  trailing?: boolean;
}

/**
 * A throttled function with additional control methods
 */
export interface ThrottledFunction<TArgs extends any[], TReturn> {
  (...args: TArgs): TReturn | undefined;

  /**
   * Cancel any pending executions
   */
  cancel(): void;

  /**
   * Immediately execute any pending call and cancel the timer
   */
  flush(): TReturn | undefined;
}

/**
 * Creates a throttled function that only invokes the provided function at most once per every `wait` milliseconds.
 * The throttled function comes with a `cancel` method to cancel delayed invocations and a `flush` method
 * to immediately invoke them.
 *
 * @template TArgs - The argument types of the function to throttle
 * @template TReturn - The return type of the function to throttle
 * @param func - The function to throttle
 * @param wait - The number of milliseconds to throttle invocations to
 * @param options - The options object to configure throttling behavior
 * @returns Returns the new throttled function with cancel and flush methods
 *
 * @example
 * // Basic usage - throttle API calls
 * const throttledFetch = throttle(fetchData, 1000);
 * throttledFetch(); // Executes immediately
 * throttledFetch(); // Ignored (within 1000ms)
 * throttledFetch(); // Ignored (within 1000ms)
 * // After 1000ms, if there were additional calls, the last one executes
 *
 * @example
 * // Throttle scroll handler with custom options
 * const handleScroll = throttle(
 *   () => console.log('Scrolling...'),
 *   200,
 *   { leading: false, trailing: true }
 * );
 * window.addEventListener('scroll', handleScroll);
 *
 * @example
 * // Using cancel and flush methods
 * const throttledSave = throttle(saveDocument, 2000);
 * throttledSave();
 * // Later, cancel pending execution
 * throttledSave.cancel();
 * // Or force immediate execution
 * throttledSave.flush();
 */
export function throttle<TArgs extends any[], TReturn>(
  func: (...args: TArgs) => TReturn,
  wait: number,
  options: ThrottleOptions = {},
): ThrottledFunction<TArgs, TReturn> {
  if (typeof func !== "function") {
    throw new TypeError("Expected a function");
  }

  if (typeof wait !== "number" || wait < 0) {
    throw new TypeError("Expected wait to be a non-negative number");
  }

  const { leading = true, trailing = true } = options;

  let lastCallTime: number | undefined;
  let lastInvokeTime = 0;
  let timerId: NodeJS.Timeout | undefined;
  let lastArgs: TArgs | undefined;
  let lastThis: any;
  let result: TReturn | undefined;

  function invokeFunc(time: number): TReturn {
    const args = lastArgs!;
    const thisArg = lastThis;

    lastArgs = undefined;
    lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time: number): TReturn | undefined {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time: number): number {
    const timeSinceLastCall = time - (lastCallTime || 0);
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;

    return timeWaiting;
  }

  function shouldInvoke(time: number): boolean {
    const timeSinceLastCall = time - (lastCallTime || 0);
    const timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (
      lastCallTime === undefined ||
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      timeSinceLastInvoke >= wait
    );
  }

  function timerExpired(): TReturn | undefined {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
    return undefined;
  }

  function trailingEdge(time: number): TReturn | undefined {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = undefined;
    lastThis = undefined;
    return result;
  }

  function cancel(): void {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = undefined;
    lastCallTime = undefined;
    lastThis = undefined;
    timerId = undefined;
  }

  function flush(): TReturn | undefined {
    return timerId === undefined ? result : trailingEdge(Date.now());
  }

  function throttled(this: any, ...args: TArgs): TReturn | undefined {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }

  throttled.cancel = cancel;
  throttled.flush = flush;

  return throttled;
}
